import { useRef, useState } from 'react';
import { MAX_AVOID_DIRECTION_CHARS, type AvoidDirectionMedium, type Creation } from '@djgg/creations';
import { useServices } from '../services/context.js';
import { useAttributedAction } from '../hooks/useAttributedAction.js';

const MEDIUM_LABEL: Record<AvoidDirectionMedium, { title: string; placeholder: string; what: string }> = {
  visual: { title: 'Avoid this visual direction', placeholder: 'muddy browns, photoreal faces, text in the image', what: 'look' },
  audio: { title: 'Avoid this audio direction', placeholder: 'slap bass, whistling, reverb-heavy pads', what: 'sound' },
};

/**
 * Plan row 1.8: the editable "avoid this direction" preference, ONE editor
 * mounted where it steers (the composer's Generate step). It is labelled for
 * what it is — steering folded into every Studio generation — and for what it
 * is not: it cannot guarantee a semantically similar result never recurs. The
 * enforceable block is exact-byte rejection on the candidate, and the copy
 * points there rather than implying this field blocks anything.
 */
export function AvoidDirectionField({ creation, medium }: { creation: Creation & { id: string }; medium: AvoidDirectionMedium }) {
  const services = useServices();
  const { runAttributed, attributionModal } = useAttributedAction();
  const saved = creation.avoidDirection?.[medium] ?? '';
  // An edit is pinned to the saved value it started from: when the creation's
  // value changes underneath it (another tab, another member, or our own save
  // echoing back), the field shows the new saved value — no effect, no ref.
  const [edit, setEdit] = useState<{ from: string; draft: string } | null>(null);
  const draft = edit && edit.from === saved ? edit.draft : saved;
  const setDraft = (next: string) => setEdit({ from: saved, draft: next });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const dirty = draft.trim() !== saved;
  const copy = MEDIUM_LABEL[medium];

  async function save() {
    if (inFlight.current || !dirty) return;
    inFlight.current = true; setBusy(true); setError('');
    try {
      await runAttributed(creation, async (attributeTo) => {
        await services.callable.updateCreation({
          creationId: creation.id,
          avoidDirection: { ...creation.avoidDirection, [medium]: draft.trim() },
          ...(attributeTo ? { attributeTo } : {}),
        });
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the preference.');
    } finally {
      inFlight.current = false; setBusy(false);
    }
  }

  return <div className="field" data-testid={`avoid-direction-${medium}`}>
    <label className="field__label" htmlFor={`avoid-direction-${medium}`}>{copy.title} <span className="muted">(steers, does not block)</span></label>
    <p className="muted" style={{ fontSize: 12, margin: '2px 0 6px' }}>
      Saved with this game and folded into every Studio generation as a preference. It steers future candidates;
      it cannot guarantee a similar {copy.what} never comes back. To block specific media for good, reject that
      candidate&apos;s exact bytes instead.
    </p>
    <div className="row" style={{ gap: 8 }}>
      <input id={`avoid-direction-${medium}`} className="input" value={draft} maxLength={MAX_AVOID_DIRECTION_CHARS}
        placeholder={copy.placeholder} disabled={busy} onChange={(e) => setDraft(e.target.value)} />
      <button type="button" className="btn btn--sm" disabled={busy || !dirty} onClick={() => void save()}
        data-testid={`avoid-direction-save-${medium}`}>
        {busy ? 'Saving…' : draft.trim() ? 'Save preference' : 'Clear preference'}
      </button>
    </div>
    {error && <p role="alert" className="error-text">{error}</p>}
    {attributionModal}
  </div>;
}
